import { nanoid } from "nanoid";

/**
 * Shared constants for the ERP system
 */

/**
 * Constant representing products that don't belong to any specific product group.
 * When this value is selected in the UI, it should be converted to null in the database
 * to properly work with rebate calculation logic for "other products" rules.
 */
export const ALL_OTHER_PRODUCTS = "All Other Products" as const;

/**
 * Constant representing non rebatable products.
 * When this value is selected in the UI, it should be converted to null in the database
 * to properly work with rebate calculation logic for "non rebatable products" rules.
 */
export const NON_REBATABLE_PRODUCTS = "Non Rebatable Products" as const;

/**
 * Base prefix for documents created from email body content.
 * These are internal documents for processing and should not be displayed as attachments.
 */
export const EMAIL_BODY_DOCUMENT_PREFIX = "__docified_email_body_" as const;

/**
 * Build metadata headers for UI <-> API versioning.
 */
export const UI_BUILD_HASH_HEADER = "x-application-ui-build-hash" as const;
export const UI_BUILD_TIME_HEADER = "x-application-ui-build-time" as const;
export const API_BUILD_HASH_HEADER = "x-application-api-build-hash" as const;
export const API_BUILD_TIME_HEADER = "x-application-api-build-time" as const;
export const POSTHOG_SESSION_ID_HEADER = "x-posthog-session-id" as const;

/**
 * Analytics / error-reporting property keys for build metadata.
 * Shared so Sentry tags and PostHog super-properties stay in sync
 * across frontend and backend.
 */
export const UI_BUILD_HASH_PROPERTY = "ui_build_hash" as const;
export const UI_BUILD_TIME_PROPERTY = "ui_build_time" as const;
export const UI_BUILD_TAG_PROPERTY = "ui_build_tag" as const;
export const API_BUILD_HASH_PROPERTY = "api_build_hash" as const;
export const API_BUILD_TIME_PROPERTY = "api_build_time" as const;
export const API_BUILD_TAG_PROPERTY = "api_build_tag" as const;

/**
 * Generates a unique filename for email body documents.
 * Uses a nanoid suffix to ensure uniqueness.
 */
export function generateEmailBodyDocumentFilename(): string {
  return `${EMAIL_BODY_DOCUMENT_PREFIX}${nanoid()}.html`;
}

/**
 * Checks if a filename represents an internal email body document
 * that should not be displayed as an attachment.
 */
export function isEmailBodyDocument(
  fileName: string | null | undefined,
): boolean {
  return fileName?.startsWith(EMAIL_BODY_DOCUMENT_PREFIX) ?? false;
}
